import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { NSInput } from 'bricks';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { NSInfoBanner } from 'components/NSInfoBanner/NSInfoBanner';
import useToast from 'hooks/useToast';
import { useAuth0 } from '@auth0/auth0-react';
import { ISignUpManager } from 'views/public/SignUp/useSignUpManager';
import TermsAndConditionsBlock from 'components/signup/TermsAndConditionsBlock';
import { useDeactivatedUserSupport } from 'views/public/SignUp/hooks';
import { SocialSignUpButtons } from 'views/public/SignUp/components';
import { SocialSignUpConnectionToLabel } from 'views/public/SignUp/constants';

export interface ISignupFormProps {
    signUpManager: ISignUpManager;
    showManualSignUpForm: () => void;
}

const SignupForm = ({ signUpManager, showManualSignUpForm }: ISignupFormProps) => {
    const {
        email,
        userOrgLookupData,
        socialSignUpEnabled,
        socialSignUpConnectionType,
        handleEmailChange,
        handleSocialSignUp,
        resetSignUpState,
    } = signUpManager;

    const { mutate: sendDeactivatedUserSupport } = useDeactivatedUserSupport();
    const { showSuccess, showError } = useToast();
    const { loginWithRedirect } = useAuth0();

    const isUserDisabled = userOrgLookupData?.user?.isDisabled === true;

    const hasActiveUserAndOrg = userOrgLookupData?.user
        && !isUserDisabled
        && userOrgLookupData?.org
        && !email.errorMsg;

    const hasNoUserButExistingOrg = !userOrgLookupData?.user && userOrgLookupData?.org && !email.errorMsg;

    const getButtonText = () => {
        if (hasActiveUserAndOrg) {
            return 'Start using Northspyre Deal';
        }
        if (socialSignUpConnectionType) {
            return `Continue with my ${SocialSignUpConnectionToLabel[socialSignUpConnectionType]} account`;
        }
        return 'Continue';
    };

    const isButtonDisabled = isUserDisabled || !userOrgLookupData || !email.value || !!email.errorMsg;

    const handleContactSupport = () => {
        const userEmail = userOrgLookupData?.user?.email;

        if (!userEmail) {
            console.error('User email not available');
            return;
        }

        sendDeactivatedUserSupport(
            {
                user_email: userEmail,
            },
            {
                onSuccess: () => {
                    showSuccess('A customer support representative will be in touch with you via email soon.');
                },
                onError: error => {
                    showError('Failed to send support request. Please try again or email concierge@northspyre.com directly.');
                },
            },
        );
    };

    const deactivatedAccountMessage = isUserDisabled ? (
        <span>
            This is a deactivated account. An admin will need to restore your access.
            {' '}
            <button
                type="button"
                onClick={handleContactSupport}
                className="text-primary bg-transparent border-0 p-0 font-inherit"
            >
                Contact support
            </button>
            .
        </span>
    ) : '';

    const handleButtonClick = () => {
        if (hasActiveUserAndOrg) {
            loginWithRedirect({
                authorizationParams: {
                    login_hint: email.value,
                },
            });
        } else {
            showManualSignUpForm();
        }
    };

    return (
        <div className="text-center">
            <h1 className="text-white mb-0">Create an account to see full insights.</h1>
            <TermsAndConditionsBlock />
            <div className="mx-auto mt-2">
                <NSInput
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email.value}
                    onChange={handleEmailChange}
                    className="DirectSignUp__Input"
                    autoComplete="email"
                    invalid={!!email.errorMsg}
                    showErrorMsg={!!email.errorMsg}
                    errorMsg={email.errorMsg}
                    errorMsgClassName="mx-2"
                />
            </div>
            {isUserDisabled && (
                <NSInfoBanner
                    title={deactivatedAccountMessage as any}
                    className="mt-2 NSCard__left-border--danger"
                    boldTitle={false}
                    showIcon={false}
                />
            )}
            {hasNoUserButExistingOrg && (
                <NSInfoBanner
                    title="Your organization already uses Northspyre."
                    className="mt-2 NSCard__left-border--info"
                    customIcon={faInfoCircle}
                    iconSize="sm"
                    boldTitle={false}
                />
            )}
            {hasActiveUserAndOrg && (
                <NSInfoBanner
                    title="You already use Northspyre. We've added access to Northspyre Deal."
                    className="mt-2 NSCard__left-border--info"
                    customIcon={faInfoCircle}
                    iconSize="sm"
                    boldTitle={false}
                />
            )}
            <div className="DirectSignUp__FullWidth">
                <NSButtonBase
                    outline
                    color="primary"
                    callback={handleButtonClick}
                    className="mt-2 DirectSignUp__Button heap-deal-signup-step-4-create-account-email"
                    disabled={isButtonDisabled}
                    dataTestId="createAccountButton"
                >
                    {getButtonText()}
                </NSButtonBase>
                { socialSignUpConnectionType && (
                    <div className="w-100 d-flex justify-content-center">
                        <NSButtonBase
                            callback={resetSignUpState || (() => undefined)}
                            className="DirectSignUp__LinkButton"
                            disabled={!resetSignUpState}
                            dataTestId="resetSignUpButton"
                        >
                            Change sign up method
                        </NSButtonBase>
                    </div>
                ) }
            </div>
            <div className="DirectSignUp__Divider">
                <hr className="DirectSignUp__DividerLine" />
                <span className="DirectSignUp__DividerText">OR</span>
                <hr className="DirectSignUp__DividerLine" />
            </div>
            <div className="justify-content-center d-flex">
                <SocialSignUpButtons
                    handleSocialSignUp={handleSocialSignUp}
                    enabled={socialSignUpEnabled}
                />
            </div>
        </div>
    );
};

export default SignupForm;
